import { z } from "zod";

/**
 * LES FICHIERS JOINTS D'UN JEU DE DONNEES : une CAPACITE des donnees, declaree
 * sur le jeu (`taki.fields.json`, `jeux[].fichiers`), comme un champ.
 *
 * ABSENTE, LA CAPACITE EST COUPEE, et c'est le defaut de toute app : aucun bloc
 * n'apparait, et le serveur refuse l'ajout (`preparerFichier`). Presente, elle
 * dit QUELS TYPES et QUELLE TAILLE le serveur accepte. La PRESENTATION (montrer
 * la section sur une porte, son libelle, sa place, lecture seule ou non) vit
 * ailleurs : dans les cartes de la fiche (`{ titre, fichiers: true }`).
 *
 * Cette declaration fait foi pour le serveur (Function d'ajout, donc regles
 * Storage), pour Lia et le MCP (metadonnees seulement si la capacite est
 * ouverte) et pour l'ecran. Elle ne s'ecrit nulle part ailleurs.
 */

/** Les familles de types qu'un jeu peut accepter. Absentes : tous les types. */
export const CATEGORIES_DE_FICHIERS = ["images", "pdf", "documents", "videos", "audio"] as const;
export type CategorieDeFichiers = (typeof CATEGORIES_DE_FICHIERS)[number];

/** Le libelle d'une categorie, tel que le Studio et l'ecran l'ecrivent. */
export const LIBELLES_CATEGORIES_DE_FICHIERS: Readonly<Record<CategorieDeFichiers, string>> = {
  images: "Images",
  pdf: "PDF",
  documents: "Documents",
  videos: "Vidéos",
  audio: "Audio",
};

/** Le plafond du socle, par fichier : celui des regles Storage. */
export const TAILLE_MAX_FICHIER_MO = 25;

const DOCUMENTS = [
  "application/pdf",
  "text/plain",
  "text/csv",
  "text/markdown",
  "application/rtf",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
];
const DOCUMENTS_PREFIXES = [
  "application/vnd.openxmlformats-officedocument.",
  "application/vnd.oasis.opendocument.",
];

/** Le contenu de `jeux[].fichiers`. */
export const fichiersDeJeuSchema = z
  .object({
    /** Les familles de types acceptees. Absente : tous les types. */
    types: z.array(z.enum(CATEGORIES_DE_FICHIERS)).min(1).optional(),
    /** La taille maximale d'UN fichier, en Mo. Absente : le plafond du socle. */
    tailleMaxMo: z.number().int().min(1).max(TAILLE_MAX_FICHIER_MO).optional(),
  })
  .strict();

export type FichiersDeJeu = z.infer<typeof fichiersDeJeuSchema>;

/** La taille maximale d'un fichier, en octets. */
export function tailleMaxOctets(fichiers: FichiersDeJeu): number {
  return (fichiers.tailleMaxMo ?? TAILLE_MAX_FICHIER_MO) * 1024 * 1024;
}

function categorieAccepte(categorie: CategorieDeFichiers, mime: string): boolean {
  switch (categorie) {
    case "images": return mime.startsWith("image/");
    case "videos": return mime.startsWith("video/");
    case "audio": return mime.startsWith("audio/");
    case "pdf": return mime === "application/pdf";
    case "documents": return DOCUMENTS.includes(mime) || DOCUMENTS_PREFIXES.some((prefixe) => mime.startsWith(prefixe));
  }
}

/** Le type MIME (en minuscules) est-il accepte par ce jeu ? */
export function typeDeFichierAccepte(fichiers: FichiersDeJeu, mime: string): boolean {
  const type = mime.trim().toLowerCase();
  if (fichiers.types === undefined) return true;
  return fichiers.types.some((categorie) => categorieAccepte(categorie, type));
}

/** L'attribut `accept` d'un champ de choix de fichier ; vide quand tout passe. */
export function acceptDeFichiers(fichiers: FichiersDeJeu): string {
  if (fichiers.types === undefined) return "";
  const parts = new Set<string>();
  for (const categorie of fichiers.types) {
    if (categorie === "images") parts.add("image/*");
    if (categorie === "videos") parts.add("video/*");
    if (categorie === "audio") parts.add("audio/*");
    if (categorie === "pdf" || categorie === "documents") parts.add("application/pdf").add(".pdf");
    if (categorie === "documents") {
      for (const extension of [".txt", ".csv", ".md", ".rtf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".odt", ".ods", ".odp"]) parts.add(extension);
    }
  }
  return [...parts].join(",");
}

/** « Images, PDF » ou « Tous les types » : ce que l'ecran et le Studio disent. */
export function libelleDesTypes(fichiers: FichiersDeJeu): string {
  return fichiers.types === undefined
    ? "Tous les types"
    : fichiers.types.map((categorie) => LIBELLES_CATEGORIES_DE_FICHIERS[categorie]).join(", ");
}

/**
 * LA POLITIQUE D'UNE FICHE quand plusieurs jeux la decrivent (deux apps
 * installees sur la meme famille) : la plus large. Un jeu sans `fichiers` ne
 * retire rien a un autre qui l'ouvre. `null` : aucun jeu ne l'ouvre.
 */
export function politiquesFusionnees(politiques: readonly (FichiersDeJeu | undefined)[]): FichiersDeJeu | null {
  const ouvertes = politiques.filter((politique): politique is FichiersDeJeu => politique !== undefined);
  if (ouvertes.length === 0) return null;
  const tousTypes = ouvertes.some((politique) => politique.types === undefined);
  const types = tousTypes ? undefined : [...new Set(ouvertes.flatMap((politique) => politique.types ?? []))];
  const tailles = ouvertes.map((politique) => politique.tailleMaxMo ?? TAILLE_MAX_FICHIER_MO);
  const tailleMaxMo = Math.max(...tailles);
  return {
    ...(types === undefined ? {} : { types: CATEGORIES_DE_FICHIERS.filter((categorie) => types.includes(categorie)) }),
    ...(tailleMaxMo === TAILLE_MAX_FICHIER_MO ? {} : { tailleMaxMo }),
  };
}

/** Un jeu decrit-il une fiche de cette famille et de ce type ? Un jeu sans type les couvre tous. */
export function jeuCouvreLaFiche(jeu: { family: string; type?: string | undefined }, family: string, type: string | undefined | null): boolean {
  return jeu.family === family && (jeu.type === undefined || jeu.type === (type ?? undefined));
}

/**
 * LE CHATTER EST-IL ALLUME pour les fiches de cette famille et de ce type
 * (vague du soir, R9) : faux des qu'un jeu qui couvre la fiche l'eteint
 * (`chatter: false`). Sans jeu declare : allume, le defaut.
 */
export function chatterActifSurLeJeu(
  champs: { jeux: ReadonlyArray<{ family: string; type?: string | undefined; chatter?: false | undefined }> },
  jeu: { family: string; type?: string | undefined } | null,
): boolean {
  if (jeu === null) return false;
  return !champs.jeux.some((declare) => jeuCouvreLaFiche(declare, jeu.family, jeu.type) && declare.chatter === false);
}

/**
 * LA COULEUR ET LA PHOTO DE REFERENCE d'une fiche de cette famille et de ce type
 * (`docs/socle/couleur-photo-fiche.md`) : allumees des qu'UN jeu qui couvre la
 * fiche les active (`couleur: true`, `photo: true`), comme les fichiers joints
 * prennent la politique la plus large. Sans jeu : eteintes, le defaut.
 */
export function identiteDuJeu(
  champs: { jeux: ReadonlyArray<{ family: string; type?: string | undefined; couleur?: true | undefined; photo?: true | undefined }> },
  jeu: { family: string; type?: string | undefined } | null,
): { couleur: boolean; photo: boolean } {
  if (jeu === null) return { couleur: false, photo: false };
  const couvrants = champs.jeux.filter((declare) => jeuCouvreLaFiche(declare, jeu.family, jeu.type));
  return {
    couleur: couvrants.some((declare) => declare.couleur === true),
    photo: couvrants.some((declare) => declare.photo === true),
  };
}

/** La taille maximale d'une photo de reference ENVOYEE, en Mo (l'ecran la reduit avant). */
export const TAILLE_MAX_PHOTO_MO = 5;

/** Les types d'image qu'une photo de reference accepte. */
export const TYPES_DE_PHOTO: readonly string[] = Object.freeze(["image/jpeg", "image/png", "image/webp", "image/gif"]);

/** Le type MIME est-il celui d'une photo de reference acceptee ? */
export function typeDePhotoAccepte(mime: string): boolean {
  return TYPES_DE_PHOTO.includes(mime.trim().toLowerCase());
}
